/**
 * The Publish workspace (W2-09 publish-import; DESIGN.md §4.16; slots `publish.left` | `publish.right`):
 *
 *   left:  CHECK (problems by severity with links), COMPATIBILITY (Phira / RPE), STATS (notes, lines, keys, length,
 *          changed since the last export)
 *   right: PACKAGE (song-select details) and EXPORT (target, what a bare chart.json plays differently and the speed
 *          bake, Advanced: profile, names, RPE version override, speed mode read-only with a link to Project
 *          settings, package options); Export (Ctrl+E) and Copy chart.json in a bar kept at the bottom of the panel
 */

import { useEffect, useRef, useState } from 'react';
import type { AppServices, PanelProps } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { ExportProfile, PackageSettings } from '../../core/model/types';
import type { RecordPatch } from '../../core/ops/types';
import { isNumericId } from '../../core/util/ids';
import type { MessageRef } from '../../core/util/types';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { Section } from '../../ui/Section';
import { Select } from '../../ui/Select';
import type { DocStore } from '../../state/types';
import { ChartOnlyNotice } from './ChartOnlyNotice';
import { CheckSummary } from './CheckSummary';
import { Compatibility } from './Compatibility';
import { runCopyChart } from './copyChart';
import { useStore, useText, type Text } from './hooks';
import { usePublishModel, type PublishModel } from './model';
import { PackageForm } from './PackageForm';
import { chartStats, formatLength } from './preflight';
import { publishSession, writePref, type ExportPrefs, type PublishTarget } from './prefs';
import { m } from './publish.messages';
import s from './Publish.module.css';

// ---------------------------------------------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------------------------------------------

/** "just now", "5 minutes ago", ... */
export function timeAgo(text: Pick<Text, 't'>, at: number, now: number): string {
  const sec = Math.max(0, (now - at) / 1000);
  if (sec < 60) return text.t(m.timeJustNow);
  const min = Math.floor(sec / 60);
  if (min < 60) return text.t(m.timeMinutes, { n: min });
  const h = Math.floor(min / 60);
  if (h < 48) return text.t(m.timeHours, { n: h });
  return text.t(m.timeDays, { n: Math.floor(h / 24) });
}

function NoProject(props: { readonly region: string }) {
  return (
    <div className={s.panel} data-region={props.region}>
      <EmptyState icon="Upload" title={{ key: m.noProjectTitle }} body={{ key: m.noProjectBody }} />
    </div>
  );
}

/** One undoable change of the package's export settings (profile, names). */
export function patchPkg(
  services: Pick<AppServices, 'ui'>,
  store: DocStore,
  field: 'profile' | 'naming',
  patch: RecordPatch<Omit<PackageSettings, 'speedMode'>>,
): void {
  const out = store.transact({ label: { key: m.txnEditPkg, params: { field } }, command: 'publish.details' }, (tx) => {
    Core.doc.patchPkg(tx, { patch });
  });
  if (!out.ok) services.ui.toast(out.reason, { kind: 'warning' });
}

/** The chart's last event time (Phira's max_time without its extra second), or null without an engine. */
export function chartEndSec(engine: AppServices['engine']): number | null {
  if (engine === null) return null;
  try {
    return engine.maxTime - 1;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------------------------------------------

export function Stats(props: { readonly services: AppServices; readonly model: PublishModel; readonly text: Text }) {
  const { services, model, text } = props;
  const { store } = model;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  const st = chartStats(store.doc, store.seq, model.songSec, chartEndSec(services.engine));
  const last = st.lastExport;
  return (
    <Section title={text.t(m.statsTitle)} level={2}>
      <ul className={s.statsList} aria-label={text.t(m.statsTitle)}>
        <li>{text.t(m.statsNotes, { notes: st.notes, real: st.real, fake: st.fake })}</li>
        <li>{text.t(m.statsLines, { count: st.lines })}</li>
        <li>{text.t(m.statsKeys, { count: st.keys })}</li>
        {st.lengthSec !== null && <li>{text.t(m.statsLength, { length: formatLength(st.lengthSec) })}</li>}
      </ul>
      <p className={s.exportState} data-changed={String(st.changedSinceExport)}>
        <Icon name={last === null ? 'CircleDashed' : st.changedSinceExport ? 'CircleAlert' : 'CircleCheck'} size={14} />
        {last === null
          ? text.t(m.statsNeverExported)
          : text.t(st.changedSinceExport ? m.statsChanged : m.statsUnchanged, {
              name: last.fileName,
              when: timeAgo(text, last.at, Math.max(now, last.at)),
            })}
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Export section
// ---------------------------------------------------------------------------------------------------------------

export const TARGET_TEXT: Readonly<Record<PublishTarget, { readonly label: MessageRef; readonly help: MessageRef }>> = {
  pez: { label: { key: m.targetPez }, help: { key: m.targetPezHelp } },
  chartJson: { label: { key: m.targetChart }, help: { key: m.targetChartHelp } },
  backup: { label: { key: m.targetBackup }, help: { key: m.targetBackupHelp } },
};

/** Radio list of export targets with a line of explanation each. */
export function TargetRadios(props: {
  readonly text: Text;
  readonly name: string;
  readonly value: PublishTarget;
  onChange(t: PublishTarget): void;
}) {
  const { text } = props;
  return (
    <fieldset className={s.radioList}>
      <legend className={s.radioLegend}>{text.t(m.exportTarget)}</legend>
      {(['pez', 'chartJson', 'backup'] as const).map((t) => (
        <label key={t} className={s.radio}>
          <input
            type="radio"
            name={props.name}
            value={t}
            checked={props.value === t}
            onChange={() => {
              props.onChange(t);
            }}
          />
          <span className={s.radioText}>
            <span className={s.radioLabel}>{text.tr(TARGET_TEXT[t].label)}</span>
            <span className={s.radioHelp}>{text.tr(TARGET_TEXT[t].help)}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** RPE versions offered for the override (the project's own value is the first option). */
export const RPE_VERSION_CHOICES: readonly number[] = [140, 150, 160, 163, 170];

/** Package options shared by the Publish panel and the Export dialog. */
export function PackageOptions(props: {
  readonly services: AppServices;
  readonly text: Text;
  readonly prefs: ExportPrefs;
  readonly profile: ExportProfile;
}) {
  const { services, text, prefs } = props;
  const set = <K extends keyof ExportPrefs>(k: K, v: ExportPrefs[K]): void => {
    writePref(services.settings, k, v);
  };
  return (
    <>
      <Checkbox
        label={text.t(m.includeSidecar)}
        checked={prefs.includeSidecar || prefs.target === 'backup'}
        disabledReason={prefs.target === 'backup' ? { key: m.backupIncludes } : null}
        onChange={(v) => {
          set('includeSidecar', v);
        }}
      />
      <span className={s.muted}>{text.t(m.includeSidecarHelp)}</span>
      <Checkbox
        label={text.t(m.includeInfoTxt)}
        checked={prefs.includeInfoTxt && props.profile === 'rpe-compatible'}
        disabledReason={props.profile === 'rpe-compatible' ? null : { key: m.rpeNoInfoTxt }}
        onChange={(v) => {
          set('includeInfoTxt', v);
        }}
      />
      <Checkbox
        label={text.t(m.includeUnused)}
        checked={prefs.includeUnusedAssets || prefs.target === 'backup'}
        disabledReason={prefs.target === 'backup' ? { key: m.backupIncludes } : null}
        onChange={(v) => {
          set('includeUnusedAssets', v);
        }}
      />
      <Checkbox
        label={text.t(m.pretty)}
        checked={prefs.pretty}
        onChange={(v) => {
          set('pretty', v);
        }}
      />
      <Select<'rpe' | 'shortest'>
        label={text.t(m.floatStyle)}
        value={prefs.floatStyle}
        options={[
          { value: 'rpe', label: text.t(m.floatRpe) },
          { value: 'shortest', label: text.t(m.floatShortest) },
        ]}
        onChange={(v) => {
          set('floatStyle', v);
        }}
      />
    </>
  );
}

function Advanced(props: { readonly services: AppServices; readonly model: PublishModel; readonly text: Text }) {
  const { services, model, text } = props;
  const { store } = model;
  const pkg = store.doc.pkg;
  const override = publishSession.rpeVersionOverride(store.projectId);
  const versions = [...new Set([...RPE_VERSION_CHOICES, ...(override !== null ? [override] : [])])]
    .filter((v) => v !== pkg.rpeVersion)
    .sort((a, b) => a - b);
  const rpeIdOk = isNumericId(store.doc.meta.rpeId);
  const speedText =
    pkg.speedMode === 'legacy'
      ? text.t(m.speedClassic)
      : pkg.rpeVersion >= 170
        ? text.t(m.speed170)
        : text.t(m.speed170Legacy, { version: pkg.rpeVersion });
  const warning = model.conversion?.overrideWarning ?? null;
  return (
    <Section title={text.t(m.advanced)} level={3} collapsible defaultOpen={false}>
      <div className={s.advanced}>
        <Select<ExportProfile>
          label={text.t(m.profile)}
          value={pkg.exportProfile}
          options={[
            { value: 'rpe-compatible', label: text.t(m.profileRpe) },
            { value: 'minimal', label: text.t(m.profileMinimal) },
          ]}
          onChange={(v) => {
            patchPkg(services, store, 'profile', { exportProfile: v });
          }}
        />
        <span className={s.muted}>{text.t(m.profileHelp)}</span>
        <Select<'readable' | 'rpe-id'>
          label={text.t(m.naming)}
          value={pkg.fileNaming}
          options={[
            { value: 'readable', label: text.t(m.namingReadable) },
            {
              value: 'rpe-id',
              label: rpeIdOk ? text.t(m.namingRpe, { id: store.doc.meta.rpeId }) : text.t(m.namingRpeNoId),
              ...(rpeIdOk ? {} : { disabledReason: { key: m.namingRpeUnavailable } }),
            },
          ]}
          onChange={(v) => {
            patchPkg(services, store, 'naming', { fileNaming: v });
          }}
        />
        <Select<string>
          label={text.t(m.rpeVersion)}
          value={override === null ? 'project' : String(override)}
          options={[
            { value: 'project', label: text.t(m.rpeVersionProject, { version: pkg.rpeVersion }) },
            ...versions.map((v) => ({ value: String(v), label: String(v) })),
          ]}
          onChange={(v) => {
            publishSession.setRpeVersionOverride(store.projectId, v === 'project' ? null : Number(v));
          }}
        />
        {override !== null && <span className={s.muted}>{text.t(m.rpeVersionHelp, { version: pkg.rpeVersion })}</span>}
        {warning !== null && (
          <div className={s.warning} role="status">
            <Icon name="TriangleAlert" size={16} />
            <div>{text.tr(warning)}</div>
          </div>
        )}
        <div className={s.readOnly}>
          <span>{text.t(m.speedModeValue, { mode: speedText })}</span>
          <Button
            size="sm"
            variant="ghost"
            icon="Settings2"
            onClick={() => {
              void services.registry.run('project.settings', { section: 'speed' }, 'button');
            }}
          >
            {text.t(m.openProjectSettings)}
          </Button>
        </div>
        <span className={s.muted}>{text.t(m.speedHelp)}</span>
        <PackageOptions services={services} text={text} prefs={model.prefs} profile={pkg.exportProfile} />
      </div>
    </Section>
  );
}

/** Export... and Copy chart.json, in a bar that stays at the bottom of the panel (UX-080). */
export function ExportActions(props: {
  readonly services: AppServices;
  readonly model: PublishModel;
  readonly text: Text;
}) {
  const { services, model, text } = props;
  const [copying, setCopying] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    // Set again on every mount (StrictMode mounts, unmounts and mounts again).
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  /** Runs synchronously up to the clipboard write (it has to start during the click). */
  const copy = (): void => {
    if (copying) return;
    const running = runCopyChart(services, model.store, model, true);
    setCopying(true);
    void running.finally(() => {
      if (alive.current) setCopying(false);
    });
  };
  const blocking = model.blocking.length;
  return (
    <div className={s.actionBar} role="group" aria-label={text.t(m.exportTitle)} data-testid="publish-actions">
      <Button variant="primary" icon="PackageCheck" command="file.export" helpId="publish.export">
        {text.t(m.exportButton)}
      </Button>
      <Button icon="ClipboardCopy" disabledReason={copying ? { key: m.running } : null} onClick={copy}>
        {text.t(m.copyChart)}
      </Button>
      {blocking > 0 && <span className={s.muted}>{text.t(m.blockingCount, { count: blocking })}</span>}
    </div>
  );
}

export function ExportSection(props: {
  readonly services: AppServices;
  readonly model: PublishModel;
  readonly text: Text;
}) {
  const { services, model, text } = props;
  const target = model.prefs.target;
  return (
    <Section title={text.t(m.exportTitle)} level={2}>
      <div className={s.form}>
        <TargetRadios
          text={text}
          name="publish-target"
          value={model.prefs.target}
          onChange={(t) => {
            writePref(services.settings, 'target', t);
          }}
        />
        <ChartOnlyNotice
          services={services}
          text={text}
          model={model}
          mode={target === 'chartJson' ? 'target' : 'copy'}
        />
        <Advanced services={services} model={model} text={text} />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------------------------------------------

function LeftBody(props: { readonly services: AppServices; readonly store: DocStore; readonly text: Text }) {
  const { services, store, text } = props;
  const model = usePublishModel(services, store);
  return (
    <div className={s.panel} data-region="publish" data-help-id="publish.check">
      <div className={s.body}>
        <CheckSummary services={services} text={text} problems={model.problems} blocking={model.blocking} />
        <Compatibility text={text} report={model.compat} />
        <Stats services={services} model={model} text={text} />
      </div>
    </div>
  );
}

function RightBody(props: { readonly services: AppServices; readonly store: DocStore; readonly text: Text }) {
  const { services, store, text } = props;
  const model = usePublishModel(services, store);
  return (
    <div className={s.panel} data-region="publish">
      <div className={s.body}>
        <PackageForm services={services} store={store} text={text} songSec={model.songSec} />
        <ExportSection services={services} model={model} text={text} />
      </div>
      <ExportActions services={services} model={model} text={text} />
    </div>
  );
}

/** `publish.left`: check, compatibility, stats. */
export function PublishLeftPanel(props: PanelProps) {
  const { services } = props;
  const text = useText(services);
  const store = useStore(services);
  if (store === null) return <NoProject region="publish" />;
  return <LeftBody key={store.projectId} services={services} store={store} text={text} />;
}

/** `publish.right`: package details and export. */
export function PublishRightPanel(props: PanelProps) {
  const { services } = props;
  const text = useText(services);
  const store = useStore(services);
  if (store === null) return <NoProject region="publish" />;
  return <RightBody key={store.projectId} services={services} store={store} text={text} />;
}
